import { useQuery } from "@tanstack/react-query";

import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PlanGateOverlay } from "@/components/shared/PlanGateOverlay";
import { useAuth } from "@/contexts/AuthContext";
import { tenantKeys } from "@/lib/queryKeys";
import { getTenantCurrent } from "@/services/authService";

/**
 * Tiers WITHOUT premium "app brain" features — blocked by PaidPlanRoute.
 * These are the bare-emit tiers (literalE/tala) plus the legacy free/starter.
 * Premium features start at Ombú. Mirrors backend `!hasPaidPlan`.
 */
export function isFreePlan(planId: string | null | undefined): boolean {
  return (
    planId == null ||
    planId === "free" ||
    planId === "starter" ||
    planId === "literalE" ||
    planId === "tala" ||
    planId === "semilla"
  );
}

/** Automation tier (reports, DGI consulta, auto-boletos). Mirrors backend `hasUltraPlan`. */
export function isUltraPlan(planId: string | null | undefined): boolean {
  return planId === "ultra" || planId === "ceibo";
}

/**
 * Premium app features (invoice email presets/custom body, fiscal, etc.) — Ombú+.
 * Legacy `esencial` id is treated as Pro. Mirrors backend `hasPaidPlan`.
 */
export function isProOrUltraPlan(planId: string | null | undefined): boolean {
  return (
    planId === "pro" ||
    planId === "ultra" ||
    planId === "esencial" ||
    planId === "ombu" ||
    planId === "ceibo"
  );
}

interface PaidPlanRouteProps {
  children: JSX.Element;
}

/**
 * Wraps routes that require Pro or Ultra on the backend. Free tenants see a blurred preview and upgrade CTA.
 */
export function PaidPlanRoute({ children }: PaidPlanRouteProps): JSX.Element {
  const { user } = useAuth();
  const { data: tenant, isLoading } = useQuery({
    queryKey: tenantKeys.current(user?.tenantId ?? undefined),
    queryFn: getTenantCurrent,
    enabled: Boolean(user?.tenantId),
  });

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <LoadingLogo size="lg" />
      </div>
    );
  }

  if (isFreePlan(tenant?.planId)) {
    return <PlanGateOverlay variant="paid">{children}</PlanGateOverlay>;
  }

  return children;
}

/**
 * Ultra-only routes (e.g. DGI consulta). Non-Ultra paid tenants see a blurred preview and upgrade CTA.
 * When the tenant is on the Free plan, the parent PaidPlanRoute handles gating — this route passes through.
 */
export function UltraPlanRoute({ children }: PaidPlanRouteProps): JSX.Element {
  const { user } = useAuth();
  const { data: tenant, isLoading } = useQuery({
    queryKey: tenantKeys.current(user?.tenantId ?? undefined),
    queryFn: getTenantCurrent,
    enabled: Boolean(user?.tenantId),
  });

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <LoadingLogo size="lg" />
      </div>
    );
  }

  if (isFreePlan(tenant?.planId)) {
    return children;
  }

  if (!isUltraPlan(tenant?.planId)) {
    return <PlanGateOverlay variant="ultra">{children}</PlanGateOverlay>;
  }

  return children;
}
